import userManager from '../auth/authConfig';
import { API_CONFIG } from '../config/api.config';
import { getImpersonationIdToken, clearImpersonationSession } from '../auth/ImpersonationContext';

interface RequestConfig extends RequestInit {
  params?: Record<string, string | number | boolean>;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const impersonationToken = getImpersonationIdToken();
    if (impersonationToken) {
      const headers: HeadersInit = {
        Authorization: `Bearer ${impersonationToken}`,
        'Content-Type': 'application/json',
      };
      const brandId = import.meta.env.VITE_BRAND_ID;
      if (brandId) headers['brandId'] = brandId;
      return headers;
    }

    let user = await userManager.getUser();

    if (!user || !user.id_token) {
      throw new Error('User not authenticated or ID token missing');
    }

    if (user.expired) {
      try {
        user = await userManager.signinSilent();
      } catch {
        await userManager.signinRedirect();
        throw new Error('Session expired');
      }
    }

    if (!user || !user.id_token) {
      throw new Error('User not authenticated or ID token missing');
    }

    const headers: HeadersInit = {
      Authorization: `Bearer ${user.id_token}`,
      'Content-Type': 'application/json',
    };

    const brandId = import.meta.env.VITE_BRAND_ID;
    if (brandId) {
      headers['brandId'] = brandId;
    }

    return headers;
  }

  private buildURL(
    endpoint: string,
    params?: Record<string, string | number | boolean>,
  ): string {
    // Handle relative URLs (for proxy)
    let url: URL;
    if (this.baseURL.startsWith('http')) {
      // Absolute URL (production) — concatenate directly to preserve base path (e.g. /prod)
      const base = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
      url = new URL(`${base}${endpoint}`);
    } else {
      // Relative URL (development with proxy)
      const fullPath = `${this.baseURL}${endpoint}`;
      url = new URL(fullPath, window.location.origin);
    }

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }

    return url.toString();
  }

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const { params, ...fetchConfig } = config;

    const url = this.buildURL(endpoint, params);
    const headers = await this.getAuthHeaders();

    try {
      const response = await fetch(url, {
        ...fetchConfig,
        cache: 'no-store',
        headers: {
          ...headers,
          ...fetchConfig.headers,
        },
      });

      if (response.status === 401) {
        if (getImpersonationIdToken()) {
          clearImpersonationSession();
          window.location.replace('/');
          throw new Error('Impersonation session expired');
        }
        try {
          await userManager.signinSilent();
          const freshHeaders = await this.getAuthHeaders();
          const retryResponse = await fetch(url, {
            ...fetchConfig,
            cache: 'no-store',
            headers: { ...freshHeaders, ...fetchConfig.headers },
          });
          if (!retryResponse.ok) {
            const error = await retryResponse
              .json()
              .catch(() => ({ message: retryResponse.statusText }));
            throw new Error(error.message || `HTTP ${retryResponse.status}: ${retryResponse.statusText}`);
          }
          return retryResponse.json();
        } catch {
          await userManager.signinRedirect();
          throw new Error('Session expired');
        }
      }

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));

        const errorMessage =
          error.message || `HTTP ${response.status}: ${response.statusText}`;

        throw new Error(errorMessage);
      }

      return response.json();
    } catch (err) {
      throw err;
    }
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean>,
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_CONFIG.baseURL);
